import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/session-expired-premium.css';

function friendlyAuthMessage(raw) {
  const text = String(raw || '').trim();
  const low = text.toLowerCase();
  if (!text || low.includes('no autenticado')) {
    return 'Volvé a ingresar con tu usuario y contraseña para retomar el trabajo.';
  }
  if (low.includes('sesión') || low.includes('session')) {
    return text;
  }
  return text;
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default function SessionExpiredOverlay() {
  const { sessionExpired, user, logout, clearSessionExpired } = useAuth();
  const navigate = useNavigate();
  const open = Boolean(sessionExpired?.open);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  const displayUser = user?.username || user?.nombre || '';
  const message = friendlyAuthMessage(sessionExpired?.message);

  const handleRelogin = async () => {
    clearSessionExpired();
    try {
      await logout();
    } catch (_) { /* ignore */ }
    navigate('/login', { replace: true });
  };

  return createPortal(
    <div
      className="crm-session-expired"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-desc"
      aria-live="assertive"
    >
      <div className="crm-session-expired__card">
        <div className="crm-session-expired__glow" aria-hidden />

        <div className="crm-session-expired__chip">
          <span className="crm-session-expired__chip-dot" aria-hidden />
          Acceso protegido
        </div>

        <div className="crm-session-expired__icon-wrap" aria-hidden>
          <div className="crm-session-expired__icon-ring">
            <LockIcon />
          </div>
        </div>

        <h2 id="session-expired-title" className="crm-session-expired__title">
          Tu sesión venció
        </h2>

        <p id="session-expired-desc" className="crm-session-expired__text">
          {displayUser ? (
            <>
              Para seguir como <strong>{displayUser}</strong>, iniciá sesión de nuevo.
            </>
          ) : (
            'Por seguridad, cerramos tu sesión. Ingresá otra vez para continuar.'
          )}
        </p>

        <div className="crm-session-expired__steps">
          <div className="crm-session-expired__step">
            <span className="crm-session-expired__step-icon" aria-hidden>
              1
            </span>
            <span>{message}</span>
          </div>
          <div className="crm-session-expired__step">
            <span className="crm-session-expired__step-icon" aria-hidden>
              2
            </span>
            <span>Cerramos la sesión anterior de forma segura para proteger tu cuenta.</span>
          </div>
          <div className="crm-session-expired__step">
            <span className="crm-session-expired__step-icon" aria-hidden>
              3
            </span>
            <span>Al volver a ingresar, retomás el trabajo donde lo dejaste.</span>
          </div>
        </div>

        <button type="button" className="crm-session-expired__btn" onClick={handleRelogin}>
          Iniciar sesión
        </button>

        <p className="crm-session-expired__foot">Grupo Bienestar · CRM</p>
      </div>
    </div>,
    document.body
  );
}
